<template>
  <span
    v-if="hasSchedule"
    class="inline-flex items-center gap-1.5 rounded-full shrink-0 max-w-fit"
    :class="[sizeClasses, stateClasses]"
    role="status"
    :aria-label="label"
  >
    <span
      class="w-2 h-2 rounded-full shrink-0"
      :class="dotClass"
      aria-hidden="true"
    />
    {{ label }}
  </span>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { isAvailableNow, hasOperatingHoursSchedule } from '@/utils/availability'

const props = defineProps({
  /** Effective operating hours (same shape as public API / Template effectiveOperatingHours). */
  operatingHours: { type: Object, default: null },
  /**
   * Visual context: hero vs contact and template-specific typography.
   * t1-hero | t2-hero | t3-hero | t4-hero | t5-hero | t5-banner (dark photo hero) | contact
   */
  variant: { type: String, default: 't1-hero' },
})

const { t } = useI18n()
const now = ref(new Date())
let ticker = null
onMounted(() => {
  ticker = setInterval(() => {
    now.value = new Date()
  }, 60_000)
})
onUnmounted(() => {
  if (ticker) clearInterval(ticker)
})

const hasSchedule = computed(() => hasOperatingHoursSchedule(props.operatingHours))
const isOpen = computed(() => isAvailableNow(props.operatingHours, now.value))

const label = computed(() => (isOpen.value ? t('public.openNow') : t('public.closedNow')))

const sizeClasses = computed(() => {
  switch (props.variant) {
    case 't2-hero':
      return 'heading-utilitarian px-4 py-1 text-sm font-bold'
    case 't3-hero':
      return 'px-3 py-1 text-xs font-semibold border border-white/25 bg-white/10 backdrop-blur-sm text-white'
    case 't4-hero':
      return 'px-3 py-1 text-xs font-bold'
    case 't5-banner':
      return 'px-3 py-1.5 text-xs font-bold mb-4 backdrop-blur-sm'
    case 't5-hero':
      return 'px-3 py-1 text-xs font-bold mb-4'
    case 'menu-modal':
      return 'px-3 py-1 text-xs font-semibold'
    case 'contact':
      return 'px-3 py-1.5 text-sm font-semibold mb-6'
    case 't1-hero':
    default:
      return 'px-3 py-1 text-xs font-semibold mb-4'
  }
})

const stateClasses = computed(() => {
  if (props.variant === 't5-banner') {
    return isOpen.value
      ? 'bg-emerald-600/90 text-white ring-1 ring-inset ring-white/25'
      : 'bg-slate-950/75 text-white ring-1 ring-inset ring-white/20'
  }
  if (isOpen.value) {
    return props.variant === 't2-hero'
      ? 'bg-green-800 text-white'
      : 'bg-green-700 text-white'
  }
  return props.variant === 't2-hero'
    ? 'bg-charcoal-blue text-white'
    : 'bg-slate-600 text-white dark:bg-slate-600'
})

const dotClass = computed(() =>
  isOpen.value ? 'bg-white motion-safe:animate-pulse' : 'bg-white/50'
)
</script>
